import { useState } from "react";
import type {
  EnrollmentSteps,
  HostingHeartbeatView,
  HostingVerifyResult,
  HostingView,
  TargetField,
} from "@rca/sdk";
import { EnrollmentBody } from "./EnrollmentSteps.js";

/**
 * デプロイ UI（要件 FR-6）。
 *
 * ★ **Web UI とは別のルート。** テナントの一式が「まだ無い」段階で動く
 * 必要があり、共通基盤だけで完結する。テナントの API が落ちていても動く
 * （＝立て直せる）。
 *
 * ★ **選ぶまでメニューを出さない**（要件 FR-1）。出すと「作ったのに
 * 動かない」が起きる。この画面が出ている間、他のタブは描かれない。
 */

export interface DeployViewProps {
  readonly view: HostingView;
  readonly verify: HostingVerifyResult | null;
  readonly busy?: boolean;
  /**
   * 置き場所を決める。
   *
   * ★ **識別子も一緒に渡す。** 自分のクラウドでは、宛先が揃うまで
   * 台帳に書かせない（空のまま `enrolling` にすると、手順が
   * `<リソースグループ>` のまま顧客に届く）。
   */
  readonly onChoose: (cloud: string, target?: Readonly<Record<string, string>>) => void;
  readonly onVerify: () => void;
  /** 使える状態になったら Web UI へ。 */
  readonly onEnter: () => void;
  /**
   * 選べる立場か（管理者か）。書かなければ選べる。
   *
   * ★ **押せる画面を出さないため。** 選ぶ口（`POST /v1/hosting`）は
   * admin 限定なので、member に選択肢を見せると押した先で 403 に
   * なって行き止まりになる。テナントを切り替えて「まだ選んでいない
   * テナントの member」になると必ず通る道。
   */
  readonly canChoose?: boolean;
}

const CLOUD_LABELS: Readonly<Record<string, string>> = {
  cloudflare: "Cloudflare（既定）",
  azure: "Microsoft Azure",
  aws: "Amazon Web Services",
  gcp: "Google Cloud",
  oci: "Oracle Cloud",
};

const label = (cloud: string): string => CLOUD_LABELS[cloud] ?? cloud;

export function DeployView(props: DeployViewProps): React.JSX.Element {
  const { view } = props;
  const [manual, setManual] = useState(false);

  return (
    <div className="deploy">
      <header className="deploy-header">
        <h1>置き場所を決める</h1>
        {/* ★ マニュアルはこの画面の中から読める（要件 FR-7）。 */}
        <button type="button" className="deploy-manual-toggle" onClick={() => setManual(!manual)}>
          {manual ? "手順に戻る" : "マニュアルを読む"}
        </button>
      </header>

      {manual ? <DeployManual view={view} /> : <DeployFlow {...props} />}
    </div>
  );
}

function DeployFlow({
  view,
  verify,
  busy,
  onChoose,
  onVerify,
  onEnter,
  canChoose = true,
}: DeployViewProps): React.JSX.Element {
  if (view.hosting === null) {
    return canChoose ? (
      <Chooser
        clouds={view.clouds}
        targets={view.targets ?? {}}
        busy={busy === true}
        onChoose={onChoose}
      />
    ) : (
      <NotAllowed />
    );
  }

  if (view.hosting.status === "ready") {
    return (
      <section className="doc-section">
        <h2>使えます</h2>
        <p>
          置き場所は <strong>{label(view.hosting.cloud)}</strong> です。
        </p>
        {/* ★ **API の宛先をここで出す。** `location.origin` は共通基盤の
            URL なので、そのままコピペさせると別の場所を叩くことになる。 */}
        {view.hosting.apiBaseUrl !== null ? (
          <p className="deploy-endpoint">
            <span className="deploy-requires-label">API の宛先</span>
            <code>{view.hosting.apiBaseUrl}</code>
          </p>
        ) : null}
        {/* ★ 停滞していることを、進む前に見せる。 */}
        <StaleWarning heartbeat={view.heartbeat ?? null} />
        <button type="button" onClick={onEnter}>
          画面へ進む
        </button>
      </section>
    );
  }

  return (
    <>
      <section className="doc-section">
        <h2>{label(view.hosting.cloud)} に置きます</h2>
        {view.hosting.status === "failed" ? (
          <p className="doc-note">
            まだ足りないものがあります。下の手順を確かめてから、もう一度
            「確かめる」を押してください。
          </p>
        ) : null}
      </section>

      {view.enrollment !== null ? <EnrollmentBody enrollment={view.enrollment} /> : null}

      <section className="doc-section">
        <h3>確かめる</h3>
        <button type="button" onClick={onVerify} disabled={busy === true}>
          {busy === true ? "確かめています…" : "確かめる"}
        </button>
        {verify !== null && !verify.ok ? (
          <ul className="deploy-missing">
            {verify.missing.map((item) => (
              <li key={item.what}>
                <strong>{item.what}</strong>
                <span>{item.hint}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </>
  );
}

/**
 * 選ぶ立場ではないとき。
 *
 * ★ **選択肢を出さない。** 出すと押せてしまい、403 で終わる。
 * 待つ以外に何も無いので、誰に頼めばよいかだけを書く。
 */
function NotAllowed(): React.JSX.Element {
  return (
    <section className="doc-section">
      <h2>置き場所がまだ決まっていません</h2>
      <p>
        どこに置くかは、このテナントの<strong>管理者</strong>が決めます。
        決まるまでは使えません。
      </p>
      <p className="doc-note">
        データ（実行の記録・成果物・鍵）と、エージェントを走らせるコンテナの置き場所です。
        あとから変えられないので、管理者にご確認ください。
      </p>
    </section>
  );
}

function Chooser({
  clouds,
  targets,
  busy,
  onChoose,
}: {
  readonly clouds: readonly string[];
  readonly targets: Readonly<Record<string, readonly TargetField[]>>;
  readonly busy: boolean;
  readonly onChoose: (cloud: string, target?: Readonly<Record<string, string>>) => void;
}): React.JSX.Element {
  /** 識別子を聞いている途中のクラウド。 */
  const [asking, setAsking] = useState<string | null>(null);

  /**
   * 押されたとき。
   *
   * ★ **貰う欄があるクラウドは、その場で確定させない。** 既定の
   * クラウドは顧客の作業が無いので、そのまま確定する。
   */
  const pick = (cloud: string): void => {
    const fields = targets[cloud] ?? [];
    if (fields.length === 0) {
      onChoose(cloud, undefined);
      return;
    }
    setAsking(cloud);
  };

  if (asking !== null) {
    return (
      <TargetForm
        cloud={asking}
        fields={targets[asking] ?? []}
        busy={busy}
        onBack={() => setAsking(null)}
        onSubmit={(target) => onChoose(asking, target)}
      />
    );
  }

  return (
    <section className="doc-section">
      <h2>どこに置きますか</h2>
      <p>
        データ（実行の記録・成果物・鍵）と、エージェントを走らせるコンテナの置き場所です。
        ★ <strong>あとから変えられません。</strong>
      </p>
      <p className="doc-note">
        自分のクラウドを選ぶと、一式はお客様のご自身のサブスクリプションに立ちます。
        我々はそこに<strong>作る・消す権限を持ちません</strong>（実行に必要な分だけを
        お渡しいただきます）。
      </p>
      <ul className="deploy-clouds">
        {clouds.map((cloud) => (
          <li key={cloud}>
            <button type="button" disabled={busy} onClick={() => pick(cloud)}>
              {label(cloud)}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * 顧客の識別子を聞く。
 *
 * ★ **欄は `targets` が名乗ったものだけ。** 画面に書くと、クラウドの
 * アダプタを足したときにずれる（手順を `enrollment` から描くのと同じ）。
 *
 * ★ **秘密は聞かない。** 値は台帳に平文で残る（AC-11）。だから
 * `type="password"` の欄も作らない——作れる形にしておくと、いつか使われる。
 */
function TargetForm({
  cloud,
  fields,
  busy,
  onBack,
  onSubmit,
}: {
  readonly cloud: string;
  readonly fields: readonly TargetField[];
  readonly busy: boolean;
  readonly onBack: () => void;
  readonly onSubmit: (target: Readonly<Record<string, string>>) => void;
}): React.JSX.Element {
  const [values, setValues] = useState<Record<string, string>>({});
  // ★ 全部埋まるまで進ませない。空の宛先で固定されると、置き場所は
  // あとから変えられないので詰む。
  const filled = fields.every((field) => (values[field.name] ?? "").trim() !== "");

  return (
    <section className="doc-section">
      <h2>{label(cloud)} のどこに置きますか</h2>
      <p>
        一式を立てる先を教えてください。★ <strong>ここで聞くのは識別子だけ</strong>
        です——鍵や資格はお預かりしません。
      </p>
      <div className="stack">
        {fields.map((field) => (
          <div className="stack" key={field.name}>
            <label htmlFor={`target-${field.name}`}>{field.label}</label>
            <input
              id={`target-${field.name}`}
              type="text"
              autoComplete="off"
              value={values[field.name] ?? ""}
              onChange={(e) => setValues({ ...values, [field.name]: e.target.value })}
            />
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              {field.hint}
            </p>
          </div>
        ))}
      </div>
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button
          type="button"
          className="primary"
          disabled={busy || !filled}
          onClick={() =>
            onSubmit(
              Object.fromEntries(
                fields.map((field) => [field.name, (values[field.name] ?? "").trim()]),
              ),
            )
          }
        >
          この宛先で進む
        </button>
        {/* ★ 選び直せること。あとから変えられないので、押し間違いの逃げ道が要る。 */}
        <button type="button" disabled={busy} onClick={onBack}>
          選び直す
        </button>
      </div>
    </section>
  );
}

/**
 * マニュアル。
 *
 * ★ **手順は `enrollment` を読んで描く。** 画面と同じ `EnrollmentBody` を
 * 呼ぶので、文面が 2 か所に増えない。書き分けると必ずずれる。
 */
export function DeployManual({ view }: { readonly view: HostingView }): React.JSX.Element {
  return (
    <div className="doc">
      <section className="doc-section">
        <h2>この画面について</h2>
        <p>
          テナントごとに「どこにデータを置き、どこでエージェントを走らせるか」を決めます。
          決めるまで他の画面は出ません（作れても動かないものを作らせないためです）。
        </p>
        <ul>
          <li>
            <strong>Cloudflare（既定）</strong> — 我々の基盤に置きます。お客様の作業はありません
          </li>
          <li>
            <strong>自分のクラウド</strong> — お客様のサブスクリプションに一式が立ちます。
            データはお客様の側に残ります
          </li>
        </ul>
      </section>

      <section className="doc-section">
        <h2>我々が持つもの・持たないもの</h2>
        <ul>
          <li>
            ★ <strong>お客様の鍵は預かりません。</strong> 我々が持つのは
            「我々自身のアプリの資格」だけで、お客様ごとに増えません
          </li>
          <li>
            ★ <strong>一式を作る権限は要求しません。</strong> 作るのはお客様の作業です
          </li>
          <li>
            ★ <strong>いつでも切れます。</strong> 我々への連絡は要りません
          </li>
        </ul>
      </section>

      {view.enrollment !== null ? (
        <EnrollmentBody enrollment={view.enrollment} />
      ) : (
        <section className="doc-section">
          <h3>手順</h3>
          <p className="doc-note">
            クラウドを選ぶと、そのクラウドの手順がここに出ます。
          </p>
        </section>
      )}

      <section className="doc-section">
        <h2>うまくいかないとき</h2>
        <ul>
          <li>
            「確かめる」で足りないものが出たら、その項目の説明どおりに実行してから、
            もう一度押してください
          </li>
          <li>
            手順には管理者の権限が要るものがあります。組織によっては承認に
            時間がかかるので、先に誰に頼むかを確かめておくと早いです
          </li>
        </ul>
        {/* ★ **権限の名前を書かない。** 手順が持っているものを並べる。
            ここに書くと `CloudAccess.enrollment()` と 2 か所になり、
            片方だけ直す事故が起きる（見張りは `deploy-view.test.tsx`）。 */}
        {view.enrollment !== null ? <RequiredApprovals enrollment={view.enrollment} /> : null}
      </section>
    </div>
  );
}

/**
 * 誰の承認が要るかの一覧。
 *
 * ★ **手順が持っている `requires` を集めるだけ。** 権限の名前を
 * ここに書かない。書くと出どころが 2 つになる。
 */
function RequiredApprovals({
  enrollment,
}: {
  readonly enrollment: EnrollmentSteps;
}): React.JSX.Element {
  const needed = [...new Set([...enrollment.steps, ...enrollment.revoke].map((s) => s.requires))];
  return (
    <>
      <h3>誰に頼む必要があるか</h3>
      <ul className="deploy-approvals">
        {needed.map((requires) => (
          <li key={requires}>{requires}</li>
        ))}
      </ul>
    </>
  );
}

/**
 * 定期実行が途絶えていることを伝える。
 *
 * ★ **止まると、そのテナントの Run だけが永久に queued で残る**（設計 §9）。
 * 他のテナントは止まらないので、気付く手立てがここしか無い。
 *
 * ★ 同時実行の枠は期限つきのリースなので、詰まった Run が枠を握り続ける
 * ことは無い。だから「今すぐ全部止まる」ではなく「新しい実行が進まない」。
 */
function StaleWarning({
  heartbeat,
}: {
  readonly heartbeat: HostingHeartbeatView | null;
}): React.JSX.Element | null {
  if (heartbeat === null || !heartbeat.stale) return null;
  return (
    <p className="deploy-stale">
      <strong>この置き場所から応答がありません。</strong>
      {heartbeat.lastAt === null
        ? " まだ一度も届いていません。"
        : ` 最後に届いたのは ${heartbeat.lastAt} です。`}
      定期実行（停滞した実行を締める処理）が動いていない可能性があります。
      このテナントの新しい実行が進まなくなりますが、他のテナントには影響しません。
    </p>
  );
}
